import Box from '@material-ui/core/Box';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { computeDestructiveActions, isProjectDeletion } from './day2';

/**
 * Display-only field that surfaces destructive-change warnings on the Day-2
 * Environment page. It does not own any real input — it reads the whole form
 * (`formContext.formData`) plus the injected `_original` snapshot and renders a
 * warning box whenever the current toggles imply data loss (turning off
 * Postgres/Storage, removing an environment, etc.).
 *
 * A project deletion gets a red box instead of the yellow one: it is not a
 * heads-up about one resource, it is the whole application, and the page has
 * to look different from an ordinary edit.
 *
 * Renders nothing on Day-1 (no `_original`) or when there is nothing to warn.
 */
export const Day2WarningsField = (
  props: FieldExtensionComponentProps<unknown>,
) => {
  const fullFormData =
    ((props as any).formContext?.formData as Record<string, any>) ?? {};
  const original = fullFormData._original as Record<string, any> | undefined;
  const actions = computeDestructiveActions(original, fullFormData);
  const deletion = !!original && isProjectDeletion(fullFormData);

  if (!original || actions.length === 0) return null;

  const palette = deletion
    ? { border: '#e57373', background: '#ffebee', text: '#b71c1c' }
    : { border: '#ffb74d', background: '#fff4e5', text: '#663c00' };

  return (
    <Box
      mt={2}
      mb={1}
      p={2}
      role={deletion ? 'alert' : undefined}
      style={{
        border: `1px solid ${palette.border}`,
        borderRadius: 4,
        background: palette.background,
      }}
    >
      <div style={{ color: palette.text, fontWeight: 600, marginBottom: 8 }}>
        {deletion
          ? '🗑️ This application will be deleted'
          : '⚠️ These changes are destructive'}
      </div>
      {deletion && (
        <div style={{ color: palette.text, marginBottom: 8 }}>
          Every other change on this form is ignored while this is on.
        </div>
      )}
      <ul style={{ margin: 0, paddingLeft: 20, color: palette.text }}>
        {actions.map((a, i) => (
          <li key={i}>{a}</li>
        ))}
      </ul>
    </Box>
  );
};
